import React from 'react';
import { useNavigate } from 'react-router-dom';

// Hero "signature": the marketplace previews itself. Real, campus-grounded listings.
const HERO_LISTINGS = [
  { category: 'Textbooks', title: 'Intro to Statistics, 4th ed.', seller: 'Maya', area: 'Northeast', price: '$25', free: false },
  { category: 'Dorm Supplies', title: 'Mini-fridge — 3.2 cu ft', seller: 'Devin', area: 'Southwest', price: '$55', free: false },
  { category: 'Free Items', title: 'Desk lamp, works great', seller: 'Priya', area: 'Central', price: 'Free', free: true },
];

const STEPS = [
  { n: '01', title: 'Browse', text: 'See what students near you are selling right now.' },
  { n: '02', title: 'Request', text: 'Send a request on any listing in one tap.' },
  { n: '03', title: 'Meet', text: 'Coordinate pickup in chat — on campus, no shipping.' },
];

const TRUST = ['Campus-only', 'Verified @umass.edu', 'No platform fees', 'Meet on campus'];

function Home() {
  const navigate = useNavigate();

  return (
    <div className="uth-root">
      <style>{`
        .uth-root {
          --paper: #faf9f9;
          --card: #ffffff;
          --ink: #201d1d;
          --ink-soft: #6b6660;
          --maroon: #881c1c;
          --maroon-deep: #5e1414;
          --maroon-tint: rgba(136,28,28,0.07);
          --line: #e7e4e1;
          --display: 'Bricolage Grotesque', 'Inter', system-ui, sans-serif;
          --body: 'Inter', system-ui, sans-serif;
          --mono: 'Space Mono', ui-monospace, 'SFMono-Regular', monospace;

          min-height: 100vh;
          background: var(--paper);
          color: var(--ink);
          font-family: var(--body);
        }
        .uth-wrap { max-width: 1120px; margin: 0 auto; padding: clamp(2rem, 5vw, 4.5rem) 1.5rem 3rem; }

        /* ---- Hero ---- */
        .uth-hero {
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: clamp(2rem, 5vw, 4rem);
          align-items: center;
        }
        .uth-eyebrow {
          font-family: var(--mono);
          font-size: 0.72rem;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: var(--maroon);
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          margin-bottom: 1.4rem;
        }
        .uth-eyebrow::before {
          content: '';
          width: 22px; height: 2px; background: var(--maroon); display: inline-block;
        }
        .uth-headline {
          font-family: var(--display);
          font-weight: 700;
          font-size: clamp(2.4rem, 5.4vw, 3.85rem);
          line-height: 1.02;
          letter-spacing: -0.02em;
          margin: 0 0 1.25rem;
          color: var(--ink);
        }
        .uth-headline .uth-mark { color: var(--maroon); }
        .uth-sub {
          font-size: 1.08rem;
          line-height: 1.6;
          color: var(--ink-soft);
          max-width: 30rem;
          margin: 0 0 2rem;
        }
        .uth-cta-row { display: flex; flex-wrap: wrap; gap: 0.75rem; align-items: center; }
        .uth-btn {
          font-family: var(--display);
          font-weight: 600;
          font-size: 1rem;
          border-radius: 12px;
          cursor: pointer;
          transition: transform .2s cubic-bezier(.165,.84,.44,1), background .2s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .uth-btn-primary {
          background: var(--maroon);
          color: #fff;
          border: none;
          padding: 0.95rem 1.6rem;
          box-shadow: 0 6px 18px rgba(136,28,28,0.22);
        }
        .uth-btn-primary:hover { background: var(--maroon-deep); transform: translateY(-2px); box-shadow: 0 10px 26px rgba(136,28,28,0.3); }
        .uth-btn-ghost {
          background: transparent;
          color: var(--maroon);
          border: 1px solid rgba(136,28,28,0.28);
          padding: 0.95rem 1.4rem;
        }
        .uth-btn-ghost:hover { background: var(--maroon-tint); border-color: var(--maroon); }
        .uth-btn:focus-visible { outline: 3px solid rgba(136,28,28,0.45); outline-offset: 2px; }
        .uth-gate {
          font-family: var(--mono);
          font-size: 0.74rem;
          color: var(--ink-soft);
          margin: 1.1rem 0 0;
          letter-spacing: 0.01em;
        }

        /* ---- Hero art: pinned listing board (signature) ---- */
        .uth-board { display: flex; flex-direction: column; gap: 1rem; }
        .uth-card {
          position: relative;
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 16px;
          padding: 1.1rem 1.2rem 1rem;
          box-shadow: 0 2px 4px rgba(32,32,32,0.04);
          transition: transform .28s cubic-bezier(.165,.84,.44,1), box-shadow .28s ease;
        }
        .uth-card::before {
          content: '';
          position: absolute; top: -7px; left: 50%; transform: translateX(-50%);
          width: 12px; height: 12px; border-radius: 50%;
          background: var(--maroon);
          box-shadow: 0 2px 4px rgba(136,28,28,0.4);
        }
        .uth-card:nth-child(1) { transform: rotate(-1.6deg) translateX(-6px); }
        .uth-card:nth-child(2) { transform: rotate(1.1deg) translateX(16px); z-index: 2; }
        .uth-card:nth-child(3) { transform: rotate(-0.6deg) translateX(-2px); }
        .uth-card:hover { transform: rotate(0deg) translateY(-5px); box-shadow: 0 16px 36px rgba(32,32,32,0.12); z-index: 3; }
        .uth-card-cat {
          font-family: var(--mono);
          font-size: 0.66rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: var(--ink-soft);
        }
        .uth-card-title {
          font-family: var(--display);
          font-weight: 600;
          font-size: 1.06rem;
          margin: 0.35rem 0 0.85rem;
          color: var(--ink);
        }
        .uth-card-foot { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
        .uth-seller { display: flex; align-items: center; gap: 0.5rem; min-width: 0; }
        .uth-avatar {
          width: 26px; height: 26px; border-radius: 50%;
          background: var(--maroon); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font-family: var(--display); font-weight: 600; font-size: 0.78rem; flex-shrink: 0;
        }
        .uth-seller-meta { font-size: 0.78rem; color: var(--ink-soft); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .uth-tag {
          font-family: var(--mono);
          font-weight: 700;
          font-size: 0.82rem;
          padding: 0.25rem 0.6rem;
          border-radius: 7px;
          flex-shrink: 0;
        }
        .uth-tag-paid { background: var(--maroon); color: #fff; }
        .uth-tag-free { background: transparent; color: var(--maroon); border: 1px solid rgba(136,28,28,0.4); }

        /* ---- Trust strip ---- */
        .uth-trust {
          display: flex; flex-wrap: wrap; gap: 0.6rem 1.5rem;
          margin: clamp(2.5rem, 5vw, 3.5rem) 0;
          padding: 1.1rem 1.3rem;
          background: var(--card);
          border: 1px solid var(--line);
          border-radius: 14px;
        }
        .uth-trust-item {
          font-family: var(--mono);
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--ink);
          display: inline-flex; align-items: center; gap: 0.55rem;
        }
        .uth-trust-item::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: var(--maroon); }

        /* ---- How it works: a real sequence ---- */
        .uth-steps-head {
          font-family: var(--mono); font-size: 0.72rem; letter-spacing: 0.16em; text-transform: uppercase;
          color: var(--ink-soft); margin: 0 0 1.4rem;
        }
        .uth-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; }
        .uth-step { border-top: 2px solid var(--maroon); padding-top: 0.9rem; }
        .uth-step-num { font-family: var(--mono); font-weight: 700; font-size: 1.35rem; color: var(--maroon); }
        .uth-step-title { font-family: var(--display); font-weight: 600; font-size: 1.15rem; margin: 0.35rem 0 0.4rem; color: var(--ink); }
        .uth-step-text { font-size: 0.92rem; line-height: 1.55; color: var(--ink-soft); margin: 0; }

        /* ---- CTA band ---- */
        .uth-final {
          margin-top: clamp(2.5rem, 5vw, 3.5rem);
          padding: clamp(1.8rem, 4vw, 2.6rem);
          background: var(--maroon);
          border-radius: 20px;
          display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 1.25rem;
        }
        .uth-final-copy { color: #fff; max-width: 30rem; }
        .uth-final-copy h2 { font-family: var(--display); font-weight: 700; font-size: clamp(1.4rem, 3vw, 1.9rem); margin: 0 0 0.4rem; color: #fff; letter-spacing: -0.01em; }
        .uth-final-copy p { margin: 0; color: rgba(255,255,255,0.82); font-size: 0.95rem; line-height: 1.5; }
        .uth-btn-onmaroon { background: #fff; color: var(--maroon); border: none; padding: 0.95rem 1.6rem; }
        .uth-btn-onmaroon:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,0,0,0.25); }

        .uth-footer { margin-top: 2.5rem; font-family: var(--mono); font-size: 0.72rem; color: var(--ink-soft); letter-spacing: 0.04em; }

        /* ---- Load motion (restrained, staggered) ---- */
        @keyframes uthRise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        .uth-anim { opacity: 0; animation: uthRise .6s cubic-bezier(.165,.84,.44,1) forwards; }
        .uth-d1 { animation-delay: .05s; }
        .uth-d2 { animation-delay: .14s; }
        .uth-d3 { animation-delay: .23s; }
        .uth-d4 { animation-delay: .32s; }
        /* cards animate but keep their resting rotation */
        @keyframes uthCard1 { from { opacity: 0; transform: rotate(-1.6deg) translate(-6px, 18px); } to { opacity: 1; transform: rotate(-1.6deg) translateX(-6px); } }
        @keyframes uthCard2 { from { opacity: 0; transform: rotate(1.1deg) translate(16px, 18px); } to { opacity: 1; transform: rotate(1.1deg) translateX(16px); } }
        @keyframes uthCard3 { from { opacity: 0; transform: rotate(-0.6deg) translate(-2px, 18px); } to { opacity: 1; transform: rotate(-0.6deg) translateX(-2px); } }
        .uth-card:nth-child(1) { opacity: 0; animation: uthCard1 .6s cubic-bezier(.165,.84,.44,1) .3s forwards; }
        .uth-card:nth-child(2) { opacity: 0; animation: uthCard2 .6s cubic-bezier(.165,.84,.44,1) .4s forwards; }
        .uth-card:nth-child(3) { opacity: 0; animation: uthCard3 .6s cubic-bezier(.165,.84,.44,1) .5s forwards; }

        /* ---- Responsive ---- */
        @media (max-width: 860px) {
          .uth-hero { grid-template-columns: 1fr; }
          .uth-board { margin-top: 0.5rem; }
          .uth-card:nth-child(1), .uth-card:nth-child(2), .uth-card:nth-child(3) { transform: none; }
          .uth-steps { grid-template-columns: 1fr; }
        }

        @media (prefers-reduced-motion: reduce) {
          .uth-anim,
          .uth-card:nth-child(1), .uth-card:nth-child(2), .uth-card:nth-child(3) {
            animation: none !important;
            opacity: 1 !important;
          }
          .uth-card:nth-child(1) { transform: rotate(-1.6deg) translateX(-6px); }
          .uth-card:nth-child(2) { transform: rotate(1.1deg) translateX(16px); }
          .uth-card:nth-child(3) { transform: rotate(-0.6deg) translateX(-2px); }
          .uth-btn, .uth-card { transition: none; }
        }
      `}</style>

      <div className="uth-wrap">
        {/* Hero */}
        <section className="uth-hero">
          <div className="uth-hero-copy">
            <span className="uth-eyebrow uth-anim uth-d1">UMass Amherst · Student Marketplace</span>
            <h1 className="uth-headline uth-anim uth-d2">
              Where UMass buys, sells, and <span className="uth-mark">passes it on.</span>
            </h1>
            <p className="uth-sub uth-anim uth-d3">
              Textbooks, dorm gear, electronics, and free finds — listed by students,
              picked up on campus. No fees, no shipping, no strangers.
            </p>
            <div className="uth-cta-row uth-anim uth-d4">
              <button className="uth-btn uth-btn-primary" onClick={() => navigate('/login')}>
                Sign in with UMass email
              </button>
              <button className="uth-btn uth-btn-ghost" onClick={() => navigate('/help')}>
                See how it works
              </button>
            </div>
            <p className="uth-gate uth-anim uth-d4">Sign-in requires a valid @umass.edu address.</p>
          </div>

          <div className="uth-hero-art" aria-hidden="true">
            <div className="uth-board">
              {HERO_LISTINGS.map(item => (
                <article className="uth-card" key={item.title}>
                  <div className="uth-card-cat">{item.category}</div>
                  <h3 className="uth-card-title">{item.title}</h3>
                  <div className="uth-card-foot">
                    <div className="uth-seller">
                      <span className="uth-avatar">{item.seller.charAt(0)}</span>
                      <span className="uth-seller-meta">{item.seller} · {item.area}</span>
                    </div>
                    <span className={`uth-tag ${item.free ? 'uth-tag-free' : 'uth-tag-paid'}`}>{item.price}</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <div className="uth-trust">
          {TRUST.map(t => (
            <span className="uth-trust-item" key={t}>{t}</span>
          ))}
        </div>

        {/* How it works — a genuine sequence, so numbering earns its place */}
        <section>
          <p className="uth-steps-head">From browse to pickup</p>
          <div className="uth-steps">
            {STEPS.map(step => (
              <div className="uth-step" key={step.n}>
                <div className="uth-step-num">{step.n}</div>
                <h3 className="uth-step-title">{step.title}</h3>
                <p className="uth-step-text">{step.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="uth-final">
          <div className="uth-final-copy">
            <h2>Your campus marketplace is waiting.</h2>
            <p>Sign in with your @umass.edu email — your account is created the first time you do.</p>
          </div>
          <button className="uth-btn uth-btn-onmaroon" onClick={() => navigate('/login')}>
            Get started
          </button>
        </section>

        <p className="uth-footer">Built for the University of Massachusetts Amherst community.</p>
      </div>
    </div>
  );
}

export default Home;
